import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { SMS_REACHOUT_PROGRAM_CREDITS } from '../constants/sms.constants';

interface SmsConsentFormProps {
  type: 'signup' | 'reachout_program';
  onSuccess: () => void;
  onCancel?: () => void;
}

const SmsConsentForm: React.FC<SmsConsentFormProps> = ({ type, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [loading, setLoading] = useState(false);

  const consentText =
    type === 'reachout_program'
      ? t(
          'sms.reachoutConsentText',
          `By providing your phone number and clicking 'Submit,' you agree to receive SMS messages from ChatNexus and your AI companion. Message frequency may vary. Standard Message and Data Rates may apply. Reply STOP to opt out. Reply HELP for help. Your mobile information will not be sold or shared with third parties for promotional or marketing purposes. You will receive ${SMS_REACHOUT_PROGRAM_CREDITS} free credits upon enrollment.`,
        )
      : t(
          'sms.standardConsentText',
          "By providing your phone number and clicking 'Submit,' you agree to receive SMS messages from ChatNexus. Message frequency may vary. Standard Message and Data Rates may apply. Reply STOP to opt out. Reply HELP for help. Consent is not a condition of purchase. Your mobile information will not be sold or shared with third parties for promotional or marketing purposes.",
        );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!consentChecked) {
      alert(t('sms.mustConsent', 'You must agree to the SMS terms to continue'));
      return;
    }

    setLoading(true);
    try {
      const endpoint =
        type === 'reachout_program' ? '/sms-consent/reachout-program' : '/sms-consent/signup';

      const response = await axios.post(endpoint, { phoneNumber });

      if (type === 'reachout_program') {
        alert(
          t(
            'sms.creditsAwarded',
            `Success! You've been awarded ${response.data.creditsAwarded} free credits!`,
          ),
        );
      }

      onSuccess();
    } catch (error) {
      console.error('SMS consent error:', error);
      alert(t('sms.error', 'Failed to process SMS consent. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
        {type === 'reachout_program'
          ? t('sms.reachoutTitle', `Get ${SMS_REACHOUT_PROGRAM_CREDITS} Free Credits!`)
          : t('sms.signupTitle', 'Phone Verification')}
      </h2>

      {type === 'reachout_program' && (
        <p className="mb-4 text-purple-600 dark:text-purple-400 font-medium">
          {t(
            'sms.reachoutDescription',
            `Receive messages from your companion via SMS and earn ${SMS_REACHOUT_PROGRAM_CREDITS} free credits!`,
          )}
        </p>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label
            htmlFor="phoneNumber"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {t('sms.phoneNumberLabel', 'Phone Number')}
          </label>
          <input
            type="tel"
            id="phoneNumber"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="+1 (555) 123-4567"
            required
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600">
          <p className="text-sm text-gray-700 dark:text-gray-300">{consentText}</p>
        </div>

        <div className="mb-6 flex items-start">
          <input
            type="checkbox"
            id="consent"
            checked={consentChecked}
            onChange={(e) => setConsentChecked(e.target.checked)}
            required
            className="mt-1 h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
          />
          <label htmlFor="consent" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            {t('sms.agreeToTerms', 'I agree to receive SMS messages as described above')}
          </label>
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={!consentChecked || loading}
            className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? t('common.submitting', 'Submitting...') : t('common.submit', 'Submit')}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 py-2 px-4 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              {t('common.cancel', 'Cancel')}
            </button>
          )}
        </div>

        <div className="mt-4 flex flex-wrap justify-center gap-3 text-sm">
          <a
            href="/sms-terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-purple-600 dark:text-purple-400 hover:underline"
          >
            {t('sms.smsTerms', 'SMS Terms')}
          </a>
          <span className="text-gray-400">•</span>
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-purple-600 dark:text-purple-400 hover:underline"
          >
            {t('sms.privacyPolicy', 'Privacy Policy')}
          </a>
          <span className="text-gray-400">•</span>
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-purple-600 dark:text-purple-400 hover:underline"
          >
            {t('sms.termsOfService', 'Terms of Service')}
          </a>
        </div>
      </form>
    </div>
  );
};

export default SmsConsentForm;
